import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { DoorOpen, Plus, Loader2, Clock, FileText, CheckCircle2, Trash2, Percent } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { money } from "@/lib/format";

const blank = { enhancer_id: "", enhancer_name: "", monthly_rent: 0, monthly_mode: "none", hourly_rate: 0, appointment_pct: 0, active: true, notes: "" };

export default function RentalsPanel() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["mgr-rentals"], queryFn: () => api.get("/manager/rentals").then((r) => r.data) });
  const roster = useQuery({ queryKey: ["mgr-roster-min"], queryFn: () => api.get("/manager/me/enhancers").then((r) => r.data).catch(() => []) });
  const [form, setForm] = useState(null);

  const rosterList = (Array.isArray(roster.data) ? roster.data : []).filter((r) => r.status === "approved" || !r.status);
  const save = async () => {
    if (!form.enhancer_id) { toast.error("Choose an enhancer."); return; }
    try {
      await api.post("/manager/rentals", form);
      toast.success("Rental agreement saved.");
      setForm(null); qc.invalidateQueries({ queryKey: ["mgr-rentals"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); qc.invalidateQueries({ queryKey: ["mgr-rentals"] }); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const logHours = (a) => { const h = window.prompt(`Hours used by ${a.enhancer_name} @ ${money(a.hourly_rate)}/hr:`); if (!h) return; act(() => api.post(`/manager/rentals/${a.id}/hours`, { hours: Number(h) }), "Hours logged."); };
  const invoice = (a) => act(() => api.post(`/manager/rentals/${a.id}/invoice`), "Monthly invoice generated.");
  const remove = (a) => { if (window.confirm("Deactivate this agreement?")) act(() => api.delete(`/manager/rentals/${a.id}`), "Deactivated."); };

  return (
    <div data-testid="rentals-panel">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="font-serif text-2xl text-[#1A242B] flex items-center gap-2"><DoorOpen size={22} className="text-[#144F75]" /> Room rentals</h2>
          <p className="text-sm text-[#4A5D6B] mt-1">Combine monthly rent, hourly use and a %-of-appointment share per enhancer.</p>
        </div>
        <button onClick={() => setForm({ ...blank })} data-testid="rental-add-btn" className="eyw-btn !py-2 text-sm"><Plus size={15} /> New agreement</button>
      </div>

      {form && (
        <div className="eyw-card p-5 mb-5" data-testid="rental-form">
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-[#4A5D6B] mb-1">Enhancer</label>
              <select value={form.enhancer_id} onChange={(e) => { const r = rosterList.find((x) => (x.enhancer_id || x.id) === e.target.value); setForm({ ...form, enhancer_id: e.target.value, enhancer_name: r?.enhancer_name || r?.name || "" }); }} data-testid="rental-enhancer" className="eyw-input w-full">
                <option value="">Select…</option>
                {rosterList.map((r) => <option key={r.enhancer_id || r.id} value={r.enhancer_id || r.id}>{r.enhancer_name || r.name || r.enhancer_email}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm text-[#4A5D6B] mb-1">Monthly rent ($)</label>
              <input type="number" min="0" value={form.monthly_rent} onChange={(e) => setForm({ ...form, monthly_rent: Number(e.target.value) })} data-testid="rental-monthly" className="eyw-input w-full" />
            </div>
            <div>
              <label className="block text-sm text-[#4A5D6B] mb-1">Monthly billing</label>
              <select value={form.monthly_mode} onChange={(e) => setForm({ ...form, monthly_mode: e.target.value })} data-testid="rental-monthly-mode" className="eyw-input w-full">
                <option value="none">No monthly rent</option>
                <option value="subscription">Auto (recurring)</option>
                <option value="invoice">Manual invoice</option>
              </select>
            </div>
            <div>
              <label className="block text-sm text-[#4A5D6B] mb-1">Hourly rate ($/hr)</label>
              <input type="number" min="0" value={form.hourly_rate} onChange={(e) => setForm({ ...form, hourly_rate: Number(e.target.value) })} data-testid="rental-hourly" className="eyw-input w-full" />
            </div>
            <div>
              <label className="block text-sm text-[#4A5D6B] mb-1">% of appointment revenue</label>
              <input type="number" min="0" max="100" value={form.appointment_pct} onChange={(e) => setForm({ ...form, appointment_pct: Number(e.target.value) })} data-testid="rental-appt-pct" className="eyw-input w-full" />
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={save} data-testid="rental-save" className="eyw-btn !py-2 text-sm">Save agreement</button>
            <button onClick={() => setForm(null)} className="eyw-btn-ghost !py-2 text-sm">Cancel</button>
          </div>
        </div>
      )}

      {isLoading ? <div className="grid place-items-center py-14"><Loader2 className="animate-spin text-[#144F75]" size={24} /></div>
      : (data?.agreements || []).length === 0 ? <div className="eyw-card p-10 text-center text-[#4A5D6B]" data-testid="rentals-empty">No rental agreements yet.</div>
      : (
        <div className="space-y-3">
          {data.agreements.filter((a) => a.active).map((a) => (
            <div key={a.id} data-testid={`rental-${a.id}`} className="eyw-card p-5 flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="font-semibold text-[#1A242B]">{a.enhancer_name}</p>
                <p className="text-xs text-[#8FA89B] flex flex-wrap gap-3 mt-1">
                  {a.monthly_rent > 0 && <span><FileText size={11} className="inline" /> {money(a.monthly_rent)}/mo ({a.monthly_mode})</span>}
                  {a.hourly_rate > 0 && <span><Clock size={11} className="inline" /> {money(a.hourly_rate)}/hr</span>}
                  {a.appointment_pct > 0 && <span><Percent size={11} className="inline" /> {a.appointment_pct}% of appointments</span>}
                </p>
                <p className="text-xs text-[#5C8A72] mt-1">Collected {money(a.collected_total)} · Due {money(a.due_total)}</p>
              </div>
              <div className="flex gap-2">
                {a.hourly_rate > 0 && <button onClick={() => logHours(a)} data-testid={`rental-hours-${a.id}`} className="eyw-btn-ghost !py-1.5 !px-2 text-xs"><Clock size={13} /> Log hours</button>}
                {a.monthly_rent > 0 && a.monthly_mode === "invoice" && <button onClick={() => invoice(a)} data-testid={`rental-invoice-${a.id}`} className="eyw-btn-ghost !py-1.5 !px-2 text-xs"><FileText size={13} /> Invoice</button>}
                <button onClick={() => setForm({ ...blank, ...a })} className="eyw-btn-ghost !py-1.5 !px-2 text-xs">Edit</button>
                <button onClick={() => remove(a)} data-testid={`rental-del-${a.id}`} className="eyw-btn-ghost !py-1.5 !px-2 text-xs text-[#B45151]"><Trash2 size={13} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
